import { AnalyticsAdapter, createAnalyticsAdapter, UserVars } from '@lifeway/frontend-analytics';
import React, { useEffect, useState } from 'react';
import licenseService from '../../../services/licenseService';
import { useUser } from '../../authentication';
import { User } from '../../authentication/domain/User';
import { useOrg } from '../../org';
import { ActiveCampaignAdapter } from '../adapters/activeCampaignAdapter';
import { FullStoryAdapter } from '../adapters/fullStoryAdapter';
import { GtagAdapter } from '../adapters/gtagAdapter';
import { IntercomAdapter } from '../adapters/intercomAdapter';

type License = {
  isGridSubscription?: boolean;
  expired?: boolean;
};

type IdentityInput = {
  user: User;
  username: string;
  orgName: string;
  currentOrgRoles: string[];
  orgSub: boolean;
  curriculumSub: boolean;
  personalSub: boolean;
};

const AnalyticsContext = React.createContext<AnalyticsAdapter | null>(null);

function buildUserVars({
  user,
  username,
  orgName,
  currentOrgRoles,
  orgSub,
  curriculumSub,
  personalSub,
}: IdentityInput): UserVars {
  return {
    id: user.userId.toUpperCase(),
    email: user.logInEmail,
    fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    userName: username,
    currentOrgId: user.lastSelectedAccount,
    currentOrgName: orgName,
    currentOrgRoles,
    allRoles: currentOrgRoles,
    orgSub,
    curriculumSub,
    personalSub,
    gitHash: window.getCommitHash(),
    url: window.location.href,
  };
}

function hasGridSubscription(licenses: License[]): boolean {
  return licenses.some(license => license.isGridSubscription && !license.expired);
}

function hasCurriculumSubscription(licenses: License[]): boolean {
  return licenses.some(license => !license.isGridSubscription);
}

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const { org } = useOrg();

  const [analytics] = useState(() =>
    createAnalyticsAdapter({
      adapters: [new FullStoryAdapter(), new IntercomAdapter(), new ActiveCampaignAdapter(), new GtagAdapter()],
    })
  );

  useEffect(() => {
    if (!user?.userId) {
      analytics.anonymize();
      return;
    }

    if (!user.lastSelectedAccount || !org) {
      return;
    }

    let cancelled = false;

    Promise.all([
      licenseService.getPairedLicenses(user.lastSelectedAccount),
      licenseService.getIndividualLicenses(),
    ]).then(([pairedLicenses, individualLicenses]: [License[], unknown[]]) => {
      if (cancelled) {
        return;
      }

      const impersonatingDisplay = user.impersonatedBy ? ` impersonated by: ${user.impersonatedBy}` : '';

      analytics.identify(
        buildUserVars({
          user,
          username: `${user.displayName ?? ''}${impersonatingDisplay}`,
          orgName: org.name ?? 'No org',
          currentOrgRoles: user.permissions ?? [],
          orgSub: hasGridSubscription(pairedLicenses),
          curriculumSub: hasCurriculumSubscription(pairedLicenses),
          personalSub: individualLicenses.length > 0,
        })
      );
    });

    return () => {
      cancelled = true;
    };
  }, [analytics, user?.userId, user?.lastSelectedAccount, user?.permissions, org?.id, org?.name]);

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const analytics = React.useContext(AnalyticsContext);

  if (!analytics) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return analytics;
};
